'use client';

import { Button } from '../../components/button';
import { Drawer } from '../../components/drawer';
import { IconButton } from '../../components/icon-button';
import { Text } from '../../components/text';
import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { Textarea } from '../../components/textarea';
import { Select } from '../../components/select';
import { createSelectListCollection } from '../../components/select';
import { Combobox } from '../../components/combobox';
import { LegacyDialog } from '../../components/dialog';
import { Switch } from '../../components/switch';
import { Tooltip } from '../../components/tooltip';
import { Badge } from '../../components/badge';
import { ConfirmationDialog } from '../confirmation-dialog/ConfirmationDialog';
import { RichMarkdownEditor } from '../rich-markdown-editor';
import { StructuredPocEditor } from './StructuredPocEditor';
import { parsePocBlocksTolerant } from '@terra/shared-schemas-orchestrator';
import type {
  PocBlock,
  AssetTypeType,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import { VStack, HStack } from '@terra/ui-styled-system';
import {
  Alert01Icon,
  CheckmarkCircle02Icon,
  Tick01Icon,
  ArrowUpDownIcon,
  Edit01Icon,
  ViewIcon,
  ViewOffIcon,
  Add01Icon,
  FloppyDiskIcon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { FindingDetails } from './FindingDetails';
import { CUSTOMER_FINDING_STATUS_OPTIONS } from './customerFindingStatusOptions';
import { FindingEditDrawerCvssSection } from './FindingEditDrawerCvssSection';
import { getFindingEditExplanationError } from './finding-edit-explanation-error';
import { OwaspRiskCalculator } from '../OWASP-Risk/OwaspRiskCalculator';
import type { InternalEnrichedFinding } from '../../types';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  SignalCategorySchema,
  type FindingStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  computeEditableFieldUpdates,
  hasEditableFieldChanges,
  type EditableFields,
} from './finding-editable-fields';
import { findingsClient } from '@terra/shared-rest-clients-findings';
import { formatCategoryForDisplay } from '../../utils';
import { createListCollection } from '@ark-ui/react';
import type { SignalDto } from '@terra/shared-contracts-signals';
import { getCvssVersionFromVector } from '@terra/shared-cvss';
import type {
  CvssEnvironmentalRequirements,
  CvssMetricReason,
  CvssVersion,
} from '@terra/shared-cvss';

type SearchSignalResult = {
  id: SignalDto['id'];
  label: string;
  category?: string;
  description?: string;
  createdAt?: string;
};

export type SearchRunResult = {
  id: string;
  name: string;
  subtitle?: string;
  createdAt?: string;
  agentName?: string;
};
type SearchAssetResult = {
  id: string;
  url: string;
  method?: string;
  type: AssetTypeType;
};
const statusOptions = createSelectListCollection({
  items: CUSTOMER_FINDING_STATUS_OPTIONS,
});
const categoryOptions = createSelectListCollection({
  items: [
    { label: 'Select Category...', value: '' },
    ...SignalCategorySchema.options.map((category) => ({
      label: formatCategoryForDisplay(category),
      value: category,
    })),
  ],
});

const getPocImageIds = (pocBlocks: EditableFields['pocBlocks']): Set<string> =>
  new Set(
    parsePocBlocksTolerant(pocBlocks ?? [])
      .filter(
        (
          block,
        ): block is Extract<
          PocBlock,
          {
            type: 'image';
            imageId: string;
          }
        > =>
          block.type === 'image' &&
          typeof block.imageId === 'string' &&
          block.imageId.length > 0,
      )
      .map((block) => block.imageId),
  );
const getDiscardedPocImageIds = ({
  editableFields,
  originalFields,
}: {
  editableFields: EditableFields;
  originalFields: EditableFields;
}): string[] => {
  const currentImageIds = getPocImageIds(editableFields.pocBlocks);
  const originalImageIds = getPocImageIds(originalFields.pocBlocks);

  return [...currentImageIds].filter(
    (imageId) => !originalImageIds.has(imageId),
  );
};

interface FindingEditDrawerProps {
  finding: InternalEnrichedFinding | null;
  open: boolean;
  onClose: () => void;
  dateFormatter?: {
    fromString: (date: string) => string;
  };
  onSave?: (
    findingId: string,
    updates: Partial<EditableFields>,
  ) => Promise<{
    success: boolean;
    error?: string;
  }>;
  mode?: 'view' | 'edit';
  onModeChange?: (mode: 'view' | 'edit') => void;
  onSearchSignals?: (query: string) => Promise<SearchSignalResult[]>;
  /** Unified search for both executions and traces */
  onSearchRuns?: (query: string) => Promise<SearchRunResult[]>;
  onSearchAssets?: (query: string) => Promise<SearchAssetResult[]>;
  /** When true (e.g. HITL edit), only narrative blocks can be added; evidence add is hidden */
  narrativesOnly?: boolean;
  /** When 'inline', renders without Drawer shell for use inside tabs */
  renderMode?: 'drawer' | 'inline';
  allowSubmitWithoutChanges?: boolean;
  submitLabel?: string;
  unchangedLabel?: string;
  onReject?: () => void;
  defaultCvssVersion?: CvssVersion;
  cvssSection?: 'shown' | 'hidden';
  environmentalRequirements?: Partial<CvssEnvironmentalRequirements> | null;
  owaspRiskEditMode?: 'editable' | 'deferred';
  onDraftUpdatesChange?: (updates: Partial<EditableFields> | null) => void;
}

export function FindingEditDrawer({
  finding,
  open,
  onClose,
  dateFormatter,
  onSave,
  mode = 'view',
  onModeChange,
  onSearchSignals,
  onSearchRuns,
  onSearchAssets,
  narrativesOnly,
  renderMode = 'drawer',
  allowSubmitWithoutChanges,
  submitLabel,
  unchangedLabel,
  onReject,
  defaultCvssVersion,
  cvssSection = 'shown',
  environmentalRequirements,
  owaspRiskEditMode = 'editable',
  onDraftUpdatesChange,
}: FindingEditDrawerProps) {
  const t = useTranslations('designSystem.findings.editDrawer');
  const tCommon = useTranslations('designSystem.common');
  const [editableFields, setEditableFields] = useState<EditableFields>({
    title: '',
    shortDescription: '',
    description: '',
    poc: '',
    pocBlocks: null,
    businessImpact: '',
    mitigationPlan: '',
    status: 'Unresolved',
    severity: 'Low',
    category: null,
    assignedTo: '',
    signalId: null,
    traceThreadId: null,
    assets: [],
    isPublished: null,
    findingUpdateComment: null,
    cvss: null,
    owaspRisk: null,
  });
  const [originalFields, setOriginalFields] = useState<EditableFields | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const isOwaspRiskComputing =
    owaspRiskEditMode === 'editable' &&
    !editableFields.owaspRisk?.level &&
    finding?.createdAt != null &&
    Date.now() - new Date(finding.createdAt).getTime() < 30 * 60 * 1000;
  const defaultDateFormatter = {
    fromString: (date: string) => new Date(date).toLocaleDateString(),
  };
  // State for search results
  const [signalSearchResults, setSignalSearchResults] = useState<
    Array<{
      id: string;
      label: string;
      category?: string;
      description?: string;
      createdAt?: string;
    }>
  >([]);
  const [runSearchResults, setRunSearchResults] = useState<SearchRunResult[]>(
    [],
  );
  const [assetSearchResults, setAssetSearchResults] = useState<
    Array<{
      id: string;
      url: string;
      method?: string;
      type: AssetTypeType;
    }>
  >([]);
  const [isSearchingSignals, setIsSearchingSignals] = useState(false);
  const [isSearchingRuns, setIsSearchingRuns] = useState(false);
  const [isSearchingAssets, setIsSearchingAssets] = useState(false);
  // Debounce timers
  const signalSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const runSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const assetSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  // Search handlers with debouncing
  const handleSignalSearch = useCallback(
    (query: string) => {
      if (signalSearchTimeout.current) {
        clearTimeout(signalSearchTimeout.current);
      }

      if (!onSearchSignals || !query.trim()) {
        setSignalSearchResults([]);

        return;
      }

      setIsSearchingSignals(true);
      signalSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await onSearchSignals(query);

          setSignalSearchResults(results);
        } catch (error) {
          console.error('Failed to search signals:', error);
          setSignalSearchResults([]);
        } finally {
          setIsSearchingSignals(false);
        }
      }, 300);
    },
    [onSearchSignals],
  );
  const handleRunSearch = useCallback(
    (query: string) => {
      if (runSearchTimeout.current) {
        clearTimeout(runSearchTimeout.current);
      }

      if (!onSearchRuns || !query.trim()) {
        setRunSearchResults([]);

        return;
      }

      setIsSearchingRuns(true);
      runSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await onSearchRuns(query);

          setRunSearchResults(results);
        } catch (error) {
          console.error('Failed to search runs:', error);
          setRunSearchResults([]);
        } finally {
          setIsSearchingRuns(false);
        }
      }, 300);
    },
    [onSearchRuns],
  );
  const handleAssetSearch = useCallback(
    (query: string) => {
      if (assetSearchTimeout.current) {
        clearTimeout(assetSearchTimeout.current);
      }

      if (!onSearchAssets || !query.trim()) {
        setAssetSearchResults([]);

        return;
      }

      setIsSearchingAssets(true);
      assetSearchTimeout.current = setTimeout(async () => {
        try {
          const results = await onSearchAssets(query);

          setAssetSearchResults(results);
        } catch (error) {
          console.error('Failed to search assets:', error);
          setAssetSearchResults([]);
        } finally {
          setIsSearchingAssets(false);
        }
      }, 300);
    },
    [onSearchAssets],
  );
  // Create collections from search results
  const signalsCollection = useMemo(() => {
    return createListCollection({
      items: signalSearchResults.map((signal) => ({
        label: signal.description || signal.label,
        value: signal.id,
        createdAt: signal.createdAt,
        category: signal.category,
      })),
    });
  }, [signalSearchResults]);
  const runsCollection = useMemo(() => {
    return createListCollection({
      items: runSearchResults.map((run) => ({
        label: run.name,
        value: run.id,
        subtitle: run.subtitle,
        createdAt: run.createdAt,
      })),
    });
  }, [runSearchResults]);
  const assetsCollection = useMemo(() => {
    return createListCollection({
      items: assetSearchResults.map((asset) => ({
        label: asset.url,
        value: asset.id,
        type: asset.type,
        method: asset.method,
      })),
    });
  }, [assetSearchResults]);

  // Cleanup timeouts on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (signalSearchTimeout.current) {
        clearTimeout(signalSearchTimeout.current);
      }

      if (runSearchTimeout.current) {
        clearTimeout(runSearchTimeout.current);
      }

      if (assetSearchTimeout.current) {
        clearTimeout(assetSearchTimeout.current);
      }
    };
  }, []);
  useEffect(() => {
    if (finding) {
      const assetsToUse =
        finding.rawAssets && finding.rawAssets.length > 0
          ? finding.rawAssets
          : finding.assets?.map((asset) => ({
              type: asset.type,
              id: asset.id,
            })) || [];
      // Get signalId from either signal object or direct signalId field
      const resolvedSignalId = finding.signal?.id || finding.signalId || null;
      const fields: EditableFields = {
        title: finding.title || '',
        shortDescription: finding.shortDescription ?? '',
        description: finding.description ?? '',
        poc: finding.poc ?? '',
        pocBlocks: finding.pocBlocks ?? null,
        businessImpact: finding.businessImpact ?? '',
        mitigationPlan: finding.mitigationPlan ?? '',
        status: finding.status || 'Unresolved',
        severity: finding.severity || 'Low',
        category: finding.category || null,
        assignedTo: finding.assignedTo || '',
        assets: assetsToUse,
        signalId: resolvedSignalId,
        traceThreadId: finding.traceThreadId || null,
        cvss: finding.cvss || null,
        owaspRisk: finding.owaspRisk || null,
        isPublished: finding.isPublished ?? null,
        findingUpdateComment: finding.findingUpdateComment ?? null,
      };

      setEditableFields(fields);
      setOriginalFields(fields);
      setHasUnsavedChanges(false);
      setSaveError(null);
      if (finding.signal && onSearchSignals) {
        // If we have full signal object, use it directly
        setSignalSearchResults([
          {
            id: finding.signal.id,
            label: finding.signal.description || '',
            category: finding.signal.category || undefined,
            description: finding.signal.description || undefined,
            createdAt: finding.signal.createdAt
              ? new Date(finding.signal.createdAt).toISOString()
              : undefined,
          },
        ]);
      } else if (resolvedSignalId && onSearchSignals) {
        // If we only have signalId, fetch signal details
        onSearchSignals(resolvedSignalId)
          .then((results) => {
            setSignalSearchResults(results);
          })
          .catch(() => {
            // Fallback: show the signalId as a minimal result
            setSignalSearchResults([
              {
                id: resolvedSignalId,
                label: resolvedSignalId,
              },
            ]);
          });
      } else {
        setSignalSearchResults([]);
      }

      if (finding.traceThreadId && onSearchRuns) {
        onSearchRuns(finding.traceThreadId)
          .then((results) => {
            setRunSearchResults(results);
          })
          .catch(() => {
            setRunSearchResults([]);
          });
      } else {
        setRunSearchResults([]);
      }

      if (finding.assets && finding.assets.length > 0 && onSearchAssets) {
        const assetPromises = finding.assets
          .filter((asset) => asset.id)
          .map(async (asset) => {
            try {
              const results = await onSearchAssets(asset.id);

              if (results && results.length > 0) {
                return results[0];
              }

              return {
                id: asset.id,
                url: asset.url || asset.id,
                method: asset.method || undefined,
                type: asset.type,
              };
            } catch {
              return {
                id: asset.id,
                url: asset.url || asset.id,
                method: asset.method || undefined,
                type: asset.type,
              };
            }
          });

        Promise.all(assetPromises).then((assets) => {
          setAssetSearchResults(assets);
        });
      } else {
        setAssetSearchResults([]);
      }
    }
  }, [finding, onSearchSignals, onSearchRuns, onSearchAssets]);
  useEffect(() => {
    if (originalFields && mode === 'edit') {
      setHasUnsavedChanges(
        hasEditableFieldChanges({ editableFields, originalFields }),
      );
    } else {
      setHasUnsavedChanges(false);
    }
  }, [editableFields, originalFields, mode]);
  useEffect(() => {
    if (!onDraftUpdatesChange) {
      return;
    }

    if (!originalFields || mode !== 'edit') {
      onDraftUpdatesChange(null);

      return;
    }

    const draftUpdates = computeEditableFieldUpdates({
      editableFields,
      originalFields,
      owaspRiskEditMode,
    });

    onDraftUpdatesChange(
      Object.keys(draftUpdates).length > 0 ? draftUpdates : null,
    );
  }, [
    editableFields,
    originalFields,
    mode,
    owaspRiskEditMode,
    onDraftUpdatesChange,
  ]);
  const handleSave = useCallback(async () => {
    if (!finding?.id || !onSave || !originalFields) return;
    setIsSaving(true);
    setSaveError(null);

    try {
      // Validate assets - ensure no assets have empty IDs
      const invalidAssets = editableFields.assets.filter(
        (asset) => !asset.id || asset.id.trim() === '',
      );

      if (invalidAssets.length > 0) {
        setSaveError(
          'Please select a valid asset for all linked assets, or remove empty entries',
        );

        return;
      }

      // Only send changed fields
      const updates = computeEditableFieldUpdates({
        editableFields,
        originalFields,
        owaspRiskEditMode,
      });
      const explanationError = getFindingEditExplanationError({
        editableFields,
        originalFields,
        cvssVersion: finding.cvssVersion,
      });

      if (explanationError) {
        setSaveError(explanationError);

        return;
      }

      if (Object.keys(updates).length === 0 && !allowSubmitWithoutChanges) {
        // No changes to save
        if (onModeChange) {
          onModeChange('view');
        }

        return;
      }

      const result = await onSave(finding.id, updates);

      if (result.success) {
        setOriginalFields({ ...editableFields });
        setHasUnsavedChanges(false);
        // Switch to view mode after successful save
        if (onModeChange) {
          onModeChange('view');
        }
      } else {
        setSaveError(result.error || 'Failed to save changes');
      }
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save changes',
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    finding?.id,
    onSave,
    originalFields,
    editableFields,
    allowSubmitWithoutChanges,
    onModeChange,
    owaspRiskEditMode,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault();
        if (
          mode === 'edit' &&
          !isSaving &&
          (hasUnsavedChanges || allowSubmitWithoutChanges) &&
          onSave
        ) {
          handleSave();
        }
      }
    };

    if (open && mode === 'edit') {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [
    open,
    mode,
    isSaving,
    hasUnsavedChanges,
    allowSubmitWithoutChanges,
    onSave,
    handleSave,
  ]);
  const handleFieldChange = <T extends keyof EditableFields>(
    field: T,
    value: EditableFields[T],
  ) => {
    setEditableFields((prev: EditableFields) => ({ ...prev, [field]: value }));
    setSaveError(null);
  };

  const handleRunChange = useCallback((traceThreadId: string | null) => {
    setEditableFields((prev) => ({
      ...prev,
      traceThreadId,
    }));
    setSaveError(null);
  }, []);
  const currentRunValue = editableFields.traceThreadId ?? null;
  const tolerantPocBlocks = editableFields.pocBlocks
    ? parsePocBlocksTolerant(editableFields.pocBlocks)
    : [];
  const structuredPocBlocks =
    tolerantPocBlocks.length > 0 ? tolerantPocBlocks : null;
  const handleCvssScoreChange = useCallback(
    (score: string, vector: string) => {
      setEditableFields((prev: EditableFields) => {
        const cvss = {
          score,
          vector,
          version:
            getCvssVersionFromVector(vector) ??
            defaultCvssVersion ??
            prev.cvss?.version ??
            '4.0',
          environmentalScore: prev.cvss?.environmentalScore ?? null,
          environmentalVector: prev.cvss?.environmentalVector ?? null,
          metricReasons: prev.cvss?.metricReasons ?? [],
          explanation: prev.cvss?.explanation ?? null,
        };
        const versionField =
          cvss.version === '4.0'
            ? 'cvss40'
            : cvss.version === '3.1'
              ? 'cvss31'
              : null;

        return {
          ...prev,
          cvss,
          ...(versionField ? { [versionField]: cvss } : {}),
        };
      });
      setSaveError(null);
    },
    [defaultCvssVersion],
  );
  const handleCvssReasonsChange = useCallback(
    ({
      metricReasons,
      explanation,
    }: {
      metricReasons: CvssMetricReason[];
      explanation: string;
    }) => {
      setEditableFields((prev: EditableFields) => {
        if (!prev.cvss) {
          return prev;
        }

        const cvss = { ...prev.cvss, metricReasons, explanation };
        const versionField =
          cvss.version === '4.0'
            ? 'cvss40'
            : cvss.version === '3.1'
              ? 'cvss31'
              : null;

        return {
          ...prev,
          cvss,
          ...(versionField
            ? {
                [versionField]: {
                  ...(prev[versionField] ?? cvss),
                  metricReasons,
                  explanation,
                },
              }
            : {}),
        };
      });
      setSaveError(null);
    },
    [],
  );
  const handleOwaspRiskScoreChange = useCallback(
    (score: string, vector: string, level: string) => {
      setEditableFields((prev: EditableFields) => ({
        ...prev,
        owaspRisk: score && vector && level ? { score, vector, level } : null,
      }));
      setSaveError(null);
    },
    [],
  );
  const handleStatusFieldChange = (value: FindingStatusType) => {
    setEditableFields((prev: EditableFields) => ({
      ...prev,
      status: value,
    }));
    setSaveError(null);
  };

  const handlePublishedFieldChange = (checked: boolean) => {
    if (checked && !editableFields.isPublished) {
      setShowPublishConfirmation(true);

      return;
    }

    handleFieldChange('isPublished', checked);
  };

  const handlePublishConfirmation = (confirmed: boolean) => {
    if (confirmed) {
      handleFieldChange('isPublished', true);
    }

    setShowPublishConfirmation(false);
  };

  const handleAddAsset = () => {
    setEditableFields((prev: EditableFields) => ({
      ...prev,
      assets: [...prev.assets, { type: 'ENDPOINT', id: '' }],
    }));
    setSaveError(null);
  };

  const handleRemoveAsset = (index: number) => {
    setEditableFields((prev: EditableFields) => ({
      ...prev,
      assets: prev.assets.filter((_, assetIndex) => assetIndex !== index),
    }));
    setSaveError(null);
  };

  const handleAssetChange = (
    index: number,
    field: 'type' | 'id',
    value: string,
  ) => {
    setEditableFields((prev: EditableFields) => ({
      ...prev,
      assets: prev.assets.map((asset, assetIndex) =>
        assetIndex === index
          ? {
              ...asset,
              [field]: field === 'type' ? (value as AssetTypeType) : value,
            }
          : asset,
      ),
    }));
    setSaveError(null);
  };

  const handleEditMode = () => {
    if (onModeChange) {
      onModeChange('edit');
    }
  };

  const cleanupDiscardedPocImages = useCallback(async () => {
    if (!finding?.id || !originalFields) {
      return;
    }

    const discardedImageIds = getDiscardedPocImageIds({
      editableFields,
      originalFields,
    });

    if (discardedImageIds.length === 0) {
      return;
    }

    await Promise.allSettled(
      discardedImageIds.map((imageId) =>
        findingsClient.deleteFindingPocImage({
          params: { id: finding.id, imageId },
        }),
      ),
    );
  }, [editableFields, finding?.id, originalFields]);
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true);

      return;
    }

    // Reset to view mode and close
    if (onModeChange) {
      onModeChange('view');
    }

    if (originalFields) {
      setEditableFields(originalFields);
      setHasUnsavedChanges(false);
    }

    setSaveError(null);
    onClose();
  };

  const handleConfirmClose = async () => {
    setShowCloseConfirmation(false);
    await cleanupDiscardedPocImages();
    if (onModeChange) {
      onModeChange('view');
    }

    if (originalFields) {
      setEditableFields(originalFields);
      setHasUnsavedChanges(false);
    }

    setSaveError(null);
    onClose();
  };

  const isInline = renderMode === 'inline';
  const headerContent = (
    <HStack w="full" justifyContent="space-between" alignItems="center" p={4}>
      <VStack alignItems="flex-start" gap={0}>
        <Text fontSize="md" fontWeight="semibold">
          {t('title')}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {t('description')}
        </Text>
      </VStack>
      <HStack gap={1}>
        {mode === 'view' && onSave && (
          <IconButton
            variant="ghost"
            size="xs"
            onClick={handleEditMode}
            aria-label={t('edit')}
          >
            <Edit01Icon size={14} />
          </IconButton>
        )}
        <IconButton variant="ghost" size="xs" onClick={handleClose}>
          <Cancel01Icon size={14} />
        </IconButton>
      </HStack>
    </HStack>
  );
  const bodyContent = finding ? (
    <VStack gap={3} alignItems="stretch" w="full" minW={0}>
      {mode === 'view' ? (
        <FindingDetails
          finding={finding}
          dateFormatter={dateFormatter || defaultDateFormatter}
        />
      ) : (
        <VStack gap={4} w="full" minW={0} alignItems="stretch">
          {saveError && (
            <Text
              p={2}
              borderRadius="md"
              fontSize="xs"
              fontWeight="medium"
              borderWidth="1px"
              borderColor="border.error"
              color="fg.error"
              bg="bg.canvas"
            >
              {saveError}
            </Text>
          )}

          {/* Basic Information Section */}
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="semibold">
                {t('sections.basicInformation.title')}
              </Text>
              <Text fontSize="2xs" color="fg.muted">
                {t('sections.basicInformation.description')}
              </Text>
            </HStack>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.basicInformation.fields.title.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.basicInformation.fields.title.helperText')}
              </Field.HelperText>
              <Input
                size="sm"
                value={editableFields.title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('title', e.target.value)
                }
                placeholder={t(
                  'sections.basicInformation.fields.title.placeholder',
                )}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.basicInformation.fields.shortDescription.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t(
                  'sections.basicInformation.fields.shortDescription.helperText',
                )}
              </Field.HelperText>
              <Textarea
                size="sm"
                value={editableFields.shortDescription ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  handleFieldChange('shortDescription', e.target.value)
                }
                placeholder={t(
                  'sections.basicInformation.fields.shortDescription.placeholder',
                )}
                rows={3}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.basicInformation.fields.description.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.basicInformation.fields.description.helperText')}
              </Field.HelperText>
              <RichMarkdownEditor
                value={editableFields.description ?? ''}
                onChange={(value) => handleFieldChange('description', value)}
                placeholder={t(
                  'sections.basicInformation.fields.description.placeholder',
                )}
                height={200}
                minHeight={150}
                maxHeight={600}
                resizable={true}
                preview="live"
              />
            </Field.Root>

            <HStack
              w="full"
              gap={3}
              alignItems="flex-start"
              p={0}
              bg="transparent"
              borderRadius="none"
              borderWidth="0"
            >
              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('sections.basicInformation.fields.status.label')}
                </Field.Label>
                <Field.HelperText fontSize="2xs" color="fg.muted">
                  {t('sections.basicInformation.fields.status.helperText')}
                </Field.HelperText>
                <Select.Root
                  positioning={{
                    sameWidth: true,
                    strategy: 'fixed',
                  }}
                  size="sm"
                  collection={statusOptions}
                  value={[editableFields.status]}
                  onValueChange={(e: { value: string[] }) =>
                    handleStatusFieldChange(e.value[0] as FindingStatusType)
                  }
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText
                        placeholder={t(
                          'sections.basicInformation.fields.status.placeholder',
                        )}
                      />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      <Select.ItemGroup>
                        {statusOptions.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            <Select.ItemText>{item.label}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.ItemGroup>
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Field.Root>

              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('sections.basicInformation.fields.severity.label')}
                </Field.Label>
                <Field.HelperText fontSize="2xs" color="fg.muted">
                  {t('sections.basicInformation.fields.severity.helperText')}
                </Field.HelperText>
                <Text
                  fontSize="sm"
                  px={2}
                  py={1.5}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="border.default"
                  h="8"
                  display="flex"
                  alignItems="center"
                >
                  {editableFields.severity ||
                    t(
                      'sections.basicInformation.fields.severity.notCalculated',
                    )}
                </Text>
              </Field.Root>
            </HStack>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.basicInformation.fields.category.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.basicInformation.fields.category.helperText')}
              </Field.HelperText>
              <Select.Root
                positioning={{
                  sameWidth: true,
                  strategy: 'fixed',
                }}
                size="sm"
                collection={categoryOptions}
                value={editableFields.category ? [editableFields.category] : []}
                onValueChange={(e: { value: string[] }) =>
                  handleFieldChange(
                    'category',
                    e.value[0] ? (e.value[0] as SignalCategoryType) : null,
                  )
                }
              >
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t(
                        'sections.basicInformation.fields.category.placeholder',
                      )}
                    />
                  </Select.Trigger>
                </Select.Control>
                <Select.Positioner>
                  <Select.Content>
                    <Select.ItemGroup>
                      {categoryOptions.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </Select.ItemGroup>
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Field.Root>
          </VStack>

          {/* Publication Section */}
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="semibold">
                {t('sections.publicationStatus.title')}
              </Text>
              <Text fontSize="2xs" color="fg.muted">
                {t('sections.publicationStatus.description')}
              </Text>
            </HStack>

            <Field.Root>
              <HStack gap={2} alignItems="center">
                <Switch
                  checked={editableFields.isPublished ?? false}
                  onCheckedChange={(details: { checked: boolean }) =>
                    handlePublishedFieldChange(details.checked)
                  }
                  size="sm"
                />

                <VStack gap={0.5} alignItems="flex-start">
                  <HStack gap={1.5} alignItems="center">
                    {editableFields.isPublished ? (
                      <ViewIcon size={14} color="green" />
                    ) : (
                      <ViewOffIcon size={14} color="gray" />
                    )}
                    <Field.Label fontSize="xs" fontWeight="medium" mb={0}>
                      {editableFields.isPublished
                        ? t('sections.publicationStatus.published')
                        : t('sections.publicationStatus.unpublished')}
                    </Field.Label>
                  </HStack>
                  <Field.HelperText fontSize="2xs" color="fg.muted">
                    {editableFields.isPublished
                      ? t('sections.publicationStatus.publishedDescription')
                      : t('sections.publicationStatus.unpublishedDescription')}
                  </Field.HelperText>
                </VStack>
              </HStack>
            </Field.Root>
          </VStack>

          {cvssSection === 'shown' && (
            <FindingEditDrawerCvssSection
              cvss={editableFields.cvss}
              originalCvss={originalFields?.cvss}
              defaultCvssVersion={defaultCvssVersion}
              environmentalRequirements={environmentalRequirements}
              onScoreChange={handleCvssScoreChange}
              onReasonsChange={handleCvssReasonsChange}
            />
          )}

          {/* OWASP Risk Rating Section */}
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="semibold">
                {t('sections.owaspRiskAssessment.title')}
              </Text>
              {owaspRiskEditMode !== 'deferred' && (
                <Text fontSize="2xs" color="fg.muted">
                  {isOwaspRiskComputing
                    ? t('sections.owaspRiskAssessment.computingDescription')
                    : t('sections.owaspRiskAssessment.description')}
                </Text>
              )}
            </HStack>

            {owaspRiskEditMode === 'deferred' ? (
              <HStack
                gap={3}
                w="full"
                alignItems="center"
                p={3}
                bg="bg.subtle"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                <Text fontSize="xs" color="fg.muted">
                  {t('sections.owaspRiskAssessment.deferredDescription')}
                </Text>
              </HStack>
            ) : (
              <OwaspRiskCalculator
                initialScore={editableFields.owaspRisk?.score || ''}
                initialVector={editableFields.owaspRisk?.vector || ''}
                onScoreChange={handleOwaspRiskScoreChange}
              />
            )}
          </VStack>

          {/* Assignment Section */}
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <Text fontSize="sm" fontWeight="semibold">
              {t('sections.assignment.title')}
            </Text>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.assignment.fields.assignedTo.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.assignment.fields.assignedTo.helperText')}
              </Field.HelperText>
              <Input
                size="sm"
                value={editableFields.assignedTo}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange('assignedTo', e.target.value)
                }
                placeholder={t(
                  'sections.assignment.fields.assignedTo.placeholder',
                )}
              />
            </Field.Root>

            <HStack
              w="full"
              gap={3}
              alignItems="flex-start"
              p={0}
              bg="transparent"
              borderRadius="none"
              borderWidth="0"
            >
              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('sections.assignment.fields.signalId.label')}
                </Field.Label>
                <Field.HelperText fontSize="2xs" color="fg.muted">
                  {t('sections.assignment.fields.signalId.helperText')}
                </Field.HelperText>
                {onSearchSignals ? (
                  <Combobox.Root
                    collection={signalsCollection}
                    value={
                      editableFields.signalId ? [editableFields.signalId] : []
                    }
                    onValueChange={(details) =>
                      handleFieldChange('signalId', details.value[0] || null)
                    }
                    onInputValueChange={(details) =>
                      handleSignalSearch(details.inputValue)
                    }
                    size="sm"
                  >
                    <Combobox.Control>
                      <Combobox.Input
                        placeholder={t(
                          'sections.assignment.fields.signalId.placeholder',
                        )}
                        asChild
                      >
                        <Input size="sm" />
                      </Combobox.Input>
                      <Combobox.Trigger asChild>
                        <IconButton
                          variant="ghost"
                          aria-label="Select Signal"
                          size="xs"
                        >
                          <ArrowUpDownIcon size={14} />
                        </IconButton>
                      </Combobox.Trigger>
                    </Combobox.Control>
                    <Combobox.Positioner>
                      <Combobox.Content maxH="300px" overflowY="auto">
                        <Combobox.List>
                          {isSearchingSignals ? (
                            <Text
                              fontSize="xs"
                              color="fg.muted"
                              p={2}
                              textAlign="center"
                            >
                              Searching...
                            </Text>
                          ) : signalsCollection.items.length > 0 ? (
                            signalsCollection.items.map((item) => (
                              <Tooltip key={item.value} title={item.label}>
                                <Combobox.Item item={item}>
                                  <VStack
                                    gap={0.5}
                                    alignItems="flex-start"
                                    flex={1}
                                    minW={0}
                                  >
                                    <Combobox.ItemText
                                      whiteSpace="nowrap"
                                      overflow="hidden"
                                      textOverflow="ellipsis"
                                      w="full"
                                    >
                                      {item.label}
                                    </Combobox.ItemText>
                                    {item.createdAt && (
                                      <Text fontSize="2xs" color="fg.muted">
                                        {(
                                          dateFormatter || defaultDateFormatter
                                        ).fromString(item.createdAt)}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Combobox.ItemIndicator>
                                    <Tick01Icon size={14} />
                                  </Combobox.ItemIndicator>
                                </Combobox.Item>
                              </Tooltip>
                            ))
                          ) : (
                            <Text
                              fontSize="xs"
                              color="fg.muted"
                              p={2}
                              textAlign="center"
                            >
                              Type to search...
                            </Text>
                          )}
                        </Combobox.List>
                      </Combobox.Content>
                    </Combobox.Positioner>
                  </Combobox.Root>
                ) : (
                  <Input
                    size="sm"
                    value={editableFields.signalId || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFieldChange(
                        'signalId',
                        e.target.value.trim() || null,
                      )
                    }
                    placeholder={t(
                      'sections.assignment.fields.signalId.placeholder',
                    )}
                  />
                )}
              </Field.Root>

              <Field.Root flex={1}>
                <Field.Label fontSize="xs" fontWeight="medium">
                  {t('sections.assignment.fields.linkedRun.label')}
                </Field.Label>
                <Field.HelperText fontSize="2xs" color="fg.muted">
                  {t('sections.assignment.fields.linkedRun.helperText')}
                </Field.HelperText>
                {onSearchRuns ? (
                  <Combobox.Root
                    collection={runsCollection}
                    value={currentRunValue ? [currentRunValue] : []}
                    onValueChange={(details) =>
                      handleRunChange(details.value[0] || null)
                    }
                    onInputValueChange={(details) =>
                      handleRunSearch(details.inputValue)
                    }
                    size="sm"
                  >
                    <Combobox.Control>
                      <Combobox.Input
                        placeholder={t(
                          'sections.assignment.fields.linkedRun.placeholder',
                        )}
                        asChild
                      >
                        <Input size="sm" />
                      </Combobox.Input>
                      <Combobox.Trigger asChild>
                        <IconButton
                          variant="ghost"
                          aria-label="Select Run"
                          size="xs"
                        >
                          <ArrowUpDownIcon size={14} />
                        </IconButton>
                      </Combobox.Trigger>
                    </Combobox.Control>
                    <Combobox.Positioner>
                      <Combobox.Content maxH="300px" overflowY="auto">
                        <Combobox.List>
                          {isSearchingRuns ? (
                            <Text
                              fontSize="xs"
                              color="fg.muted"
                              p={2}
                              textAlign="center"
                            >
                              Searching...
                            </Text>
                          ) : runsCollection.items.length > 0 ? (
                            runsCollection.items.map((item) => (
                              <Tooltip
                                key={item.value}
                                title={item.label}
                                subtitle={
                                  item.createdAt
                                    ? (
                                        dateFormatter || defaultDateFormatter
                                      ).fromString(item.createdAt)
                                    : undefined
                                }
                              >
                                <Combobox.Item item={item}>
                                  <HStack
                                    gap={2}
                                    alignItems="center"
                                    flex={1}
                                    minW={0}
                                  >
                                    <VStack
                                      gap={0.5}
                                      alignItems="flex-start"
                                      flex={1}
                                      minW={0}
                                    >
                                      <HStack gap={1} w="full">
                                        <Combobox.ItemText
                                          whiteSpace="nowrap"
                                          overflow="hidden"
                                          textOverflow="ellipsis"
                                          flex={1}
                                        >
                                          {item.label}
                                        </Combobox.ItemText>
                                        <Badge size="sm" variant="emphasis">
                                          {t(
                                            'sections.assignment.fields.linkedRun.traceLabel',
                                          )}
                                        </Badge>
                                      </HStack>
                                      {(item.subtitle || item.createdAt) && (
                                        <Text fontSize="2xs" color="fg.muted">
                                          {item.subtitle ||
                                            (item.createdAt &&
                                              (
                                                dateFormatter ||
                                                defaultDateFormatter
                                              ).fromString(item.createdAt))}
                                        </Text>
                                      )}
                                    </VStack>
                                    <Combobox.ItemIndicator>
                                      <Tick01Icon size={14} />
                                    </Combobox.ItemIndicator>
                                  </HStack>
                                </Combobox.Item>
                              </Tooltip>
                            ))
                          ) : (
                            <Text
                              fontSize="xs"
                              color="fg.muted"
                              p={2}
                              textAlign="center"
                            >
                              Type to search...
                            </Text>
                          )}
                        </Combobox.List>
                      </Combobox.Content>
                    </Combobox.Positioner>
                  </Combobox.Root>
                ) : (
                  <Input
                    size="sm"
                    value={editableFields.traceThreadId || ''}
                    disabled
                    placeholder={t(
                      'sections.assignment.fields.linkedRun.noSearchAvailable',
                    )}
                  />
                )}
              </Field.Root>
            </HStack>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.assignment.fields.assets.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.assignment.fields.assets.helperText')}
              </Field.HelperText>

              <VStack gap={2} alignItems="stretch">
                {editableFields.assets.length === 0 ? (
                  <Text fontSize="xs" color="fg.muted" py={1}>
                    {t('sections.assignment.fields.assets.noAssetsLinked')}
                  </Text>
                ) : (
                  editableFields.assets.map((asset, index) => (
                    <HStack
                      key={index}
                      gap={2}
                      alignItems="flex-start"
                      p={2}
                      borderWidth="1px"
                      borderColor="border.default"
                      borderRadius="md"
                      bg="bg.subtle"
                    >
                      <Field.Root flex={1}>
                        <Field.Label fontSize="xs" fontWeight="medium">
                          {t('sections.assignment.fields.assets.assetId')}
                        </Field.Label>
                        {onSearchAssets ? (
                          <Combobox.Root
                            collection={assetsCollection}
                            value={asset.id ? [asset.id] : []}
                            onValueChange={(details) => {
                              const selectedId = details.value[0] || '';
                              const selectedAsset = assetsCollection.items.find(
                                (item) => item.value === selectedId,
                              );

                              handleAssetChange(index, 'id', selectedId);
                              if (selectedAsset && selectedAsset.type) {
                                handleAssetChange(
                                  index,
                                  'type',
                                  selectedAsset.type,
                                );
                              }
                            }}
                            onInputValueChange={(details) =>
                              handleAssetSearch(details.inputValue)
                            }
                            size="sm"
                          >
                            <Combobox.Control>
                              <Combobox.Input
                                placeholder={t(
                                  'sections.assignment.fields.assets.enterAssetId',
                                )}
                                asChild
                              >
                                <Input size="sm" />
                              </Combobox.Input>
                              <Combobox.Trigger asChild>
                                <IconButton
                                  variant="ghost"
                                  aria-label="Select Asset"
                                  size="xs"
                                >
                                  <ArrowUpDownIcon size={14} />
                                </IconButton>
                              </Combobox.Trigger>
                            </Combobox.Control>
                            <Combobox.Positioner>
                              <Combobox.Content maxH="300px" overflowY="auto">
                                <Combobox.List>
                                  {isSearchingAssets ? (
                                    <Text
                                      fontSize="xs"
                                      color="fg.muted"
                                      p={2}
                                      textAlign="center"
                                    >
                                      Searching...
                                    </Text>
                                  ) : assetsCollection.items.length > 0 ? (
                                    assetsCollection.items.map((item) => (
                                      <Tooltip
                                        key={item.value}
                                        title={item.label}
                                      >
                                        <Combobox.Item item={item}>
                                          <HStack gap={2} flex={1} minW={0}>
                                            <HStack gap={1}>
                                              {item.type && (
                                                <Badge
                                                  size="sm"
                                                  variant="outline"
                                                >
                                                  {item.type.toLowerCase()}
                                                </Badge>
                                              )}
                                              {item.method && (
                                                <Badge
                                                  size="sm"
                                                  variant="emphasis"
                                                >
                                                  {item.method}
                                                </Badge>
                                              )}
                                            </HStack>
                                            <Combobox.ItemText
                                              whiteSpace="nowrap"
                                              overflow="hidden"
                                              textOverflow="ellipsis"
                                              flex={1}
                                            >
                                              {item.label}
                                            </Combobox.ItemText>
                                          </HStack>
                                          <Combobox.ItemIndicator>
                                            <Tick01Icon size={14} />
                                          </Combobox.ItemIndicator>
                                        </Combobox.Item>
                                      </Tooltip>
                                    ))
                                  ) : (
                                    <Text
                                      fontSize="xs"
                                      color="fg.muted"
                                      p={2}
                                      textAlign="center"
                                    >
                                      Type to search...
                                    </Text>
                                  )}
                                </Combobox.List>
                              </Combobox.Content>
                            </Combobox.Positioner>
                          </Combobox.Root>
                        ) : (
                          <Input
                            size="sm"
                            value={asset.id}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => handleAssetChange(index, 'id', e.target.value)}
                            placeholder={t(
                              'sections.assignment.fields.assets.enterAssetId',
                            )}
                          />
                        )}
                      </Field.Root>

                      <IconButton
                        variant="ghost"
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleRemoveAsset(index)}
                        aria-label={t(
                          'sections.assignment.fields.assets.removeAsset',
                        )}
                        mt="20px"
                        alignSelf="flex-start"
                      >
                        <Delete02Icon size={16} />
                      </IconButton>
                    </HStack>
                  ))
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAddAsset}
                  alignSelf="flex-start"
                  mt={editableFields.assets.length > 0 ? 2 : 0}
                >
                  <Add01Icon size={16} />
                  {t('sections.assignment.fields.assets.addAsset')}
                </Button>
              </VStack>
            </Field.Root>
          </VStack>

          {/* Technical Details Section */}
          <VStack
            gap={3}
            p={4}
            bg="bg.default"
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.default"
            w="full"
            alignItems="stretch"
          >
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="semibold">
                {t('sections.technicalDetails.title')}
              </Text>
              <Text fontSize="2xs" color="fg.muted">
                {t('sections.technicalDetails.description')}
              </Text>
            </HStack>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.technicalDetails.fields.poc.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t('sections.technicalDetails.fields.poc.helperText')}
              </Field.HelperText>
              {structuredPocBlocks ? (
                <StructuredPocEditor
                  blocks={structuredPocBlocks}
                  findingId={finding.id}
                  isDraftDirty={hasUnsavedChanges}
                  onChange={(blocks: PocBlock[]) =>
                    handleFieldChange(
                      'pocBlocks',
                      blocks.length > 0 ? blocks : null,
                    )
                  }
                  narrativesOnly={narrativesOnly}
                />
              ) : (
                <VStack gap={2} alignItems="stretch">
                  <RichMarkdownEditor
                    value={editableFields.poc ?? ''}
                    onChange={(value) => handleFieldChange('poc', value)}
                    placeholder={t(
                      'sections.technicalDetails.fields.poc.placeholder',
                    )}
                    height={200}
                    minHeight={150}
                    maxHeight={600}
                    resizable={true}
                    preview="live"
                  />
                  <Button
                    size="xs"
                    variant="outline"
                    alignSelf="flex-start"
                    onClick={() =>
                      handleFieldChange('pocBlocks', [
                        {
                          type: 'narrative',
                          content:
                            editableFields.poc?.trim() ||
                            t(
                              'sections.technicalDetails.fields.poc.placeholder',
                            ),
                        },
                      ])
                    }
                  >
                    {t(
                      'sections.technicalDetails.fields.poc.useStructuredToAddEvidences',
                    )}
                  </Button>
                </VStack>
              )}
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.technicalDetails.fields.businessImpact.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t(
                  'sections.technicalDetails.fields.businessImpact.helperText',
                )}
              </Field.HelperText>
              <RichMarkdownEditor
                value={editableFields.businessImpact ?? ''}
                onChange={(value) => handleFieldChange('businessImpact', value)}
                placeholder={t(
                  'sections.technicalDetails.fields.businessImpact.placeholder',
                )}
                height={200}
                minHeight={150}
                maxHeight={600}
                resizable={true}
                preview="live"
              />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="medium">
                {t('sections.technicalDetails.fields.mitigationPlan.label')}
              </Field.Label>
              <Field.HelperText fontSize="2xs" color="fg.muted">
                {t(
                  'sections.technicalDetails.fields.mitigationPlan.helperText',
                )}
              </Field.HelperText>
              <RichMarkdownEditor
                value={editableFields.mitigationPlan ?? ''}
                onChange={(value) => handleFieldChange('mitigationPlan', value)}
                placeholder={t(
                  'sections.technicalDetails.fields.mitigationPlan.placeholder',
                )}
                height={200}
                minHeight={150}
                maxHeight={600}
                resizable={true}
                preview="live"
              />
            </Field.Root>
          </VStack>
        </VStack>
      )}
    </VStack>
  ) : null;
  const footerContent = (
    <HStack
      gap={2}
      p={4}
      borderTopWidth="1px"
      borderTopColor="border.default"
      bg="bg.subtle"
      w="full"
      flexShrink={0}
    >
      {mode === 'edit' ? (
        <HStack
          gap={2}
          w="full"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text fontSize="2xs" color="fg.muted">
            {hasUnsavedChanges
              ? t('unsavedChanges')
              : (unchangedLabel ?? t('allChangesSaved'))}
          </Text>
          <HStack gap={2}>
            <Button
              variant="outline"
              onClick={() => {
                if (onReject) {
                  onReject();

                  return;
                }

                handleClose();
              }}
              disabled={isSaving}
              size="sm"
              colorScheme={onReject ? 'red' : 'gray'}
            >
              {onReject ? tCommon('reject') : t('cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                (!allowSubmitWithoutChanges && !hasUnsavedChanges) || isSaving
              }
              loading={isSaving}
              size="sm"
              colorScheme="blue"
            >
              {submitLabel ? (
                <CheckmarkCircle02Icon size={14} />
              ) : (
                <FloppyDiskIcon size={14} />
              )}
              {submitLabel ?? t('save')}
            </Button>
          </HStack>
        </HStack>
      ) : (
        <Button variant="outline" onClick={onClose} size="sm">
          {t('close')}
        </Button>
      )}
    </HStack>
  );
  const confirmationDialogs = (
    <>
      <LegacyDialog.Root
        open={showPublishConfirmation}
        modal={true}
        onOpenChange={(details) => {
          if (!details.open) {
            handlePublishConfirmation(false);
          }
        }}
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content
            maxW="lg"
            bg="bg.default"
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            mx={4}
          >
            <VStack gap={6} alignItems="flex-start" w="full">
              <HStack gap={4} w="full" alignItems="flex-start">
                <Alert01Icon size={24} color="orange.500" />
                <VStack alignItems="flex-start" gap={2} flex={1}>
                  <LegacyDialog.Title>
                    {t('publishConfirmation.title')}
                  </LegacyDialog.Title>
                  <LegacyDialog.Description
                    fontSize="md"
                    lineHeight="relaxed"
                    pr={8}
                  >
                    {t('publishConfirmation.message')}
                  </LegacyDialog.Description>
                </VStack>
              </HStack>

              <HStack gap={3} w="full" justifyContent="flex-end" pt={2}>
                <Button
                  variant="outline"
                  onClick={() => handlePublishConfirmation(false)}
                  size="md"
                  minW="80px"
                >
                  {t('publishConfirmation.no')}
                </Button>
                <Button
                  onClick={() => handlePublishConfirmation(true)}
                  size="md"
                  colorScheme="orange"
                  minW="80px"
                >
                  {t('publishConfirmation.yes')}
                </Button>
              </HStack>
            </VStack>

            <LegacyDialog.CloseTrigger
              asChild
              position="absolute"
              top={4}
              right={4}
            >
              <IconButton variant="ghost" size="sm">
                <Cancel01Icon size={16} />
              </IconButton>
            </LegacyDialog.CloseTrigger>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>

      <ConfirmationDialog
        isOpen={showCloseConfirmation}
        onOpenChange={({ open: isOpen }: { open: boolean }) =>
          setShowCloseConfirmation(isOpen)
        }
        title={t('confirmClose.title')}
        description={t('confirmClose.description')}
        confirmText={t('confirmClose.confirm')}
        cancelText={t('confirmClose.cancel')}
        onConfirm={handleConfirmClose}
        variant="warning"
      />
    </>
  );

  if (isInline) {
    return (
      <VStack h="full" w="full" minW={0} overflow="hidden">
        {headerContent}
        <VStack
          flex={1}
          overflowY="auto"
          overflowX="hidden"
          px={4}
          w="full"
          minW={0}
        >
          {bodyContent}
        </VStack>
        {footerContent}
        {confirmationDialogs}
      </VStack>
    );
  }

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <Drawer.Backdrop />
      <Drawer.Positioner w="60vw">
        <Drawer.Content>
          <Drawer.Header p={4}>
            <HStack w="full" justifyContent="space-between" alignItems="center">
              <VStack alignItems="flex-start" gap={0}>
                <Drawer.Title fontSize="md">{t('title')}</Drawer.Title>
                <Drawer.Description fontSize="xs">
                  {t('description')}
                </Drawer.Description>
              </VStack>
              <HStack gap={1}>
                {mode === 'view' && onSave && (
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={handleEditMode}
                    aria-label={t('edit')}
                  >
                    <Edit01Icon size={14} />
                  </IconButton>
                )}
                <Drawer.CloseTrigger asChild position="static">
                  <IconButton variant="ghost" size="xs" onClick={handleClose}>
                    <Cancel01Icon size={14} />
                  </IconButton>
                </Drawer.CloseTrigger>
              </HStack>
            </HStack>
          </Drawer.Header>
          <Drawer.Body>{bodyContent}</Drawer.Body>
          <Drawer.Footer
            gap="2"
            p={4}
            borderTopWidth="1px"
            borderTopColor="border.default"
            bg="bg.subtle"
          >
            {mode === 'edit' ? (
              <HStack
                gap={2}
                w="full"
                justifyContent="space-between"
                alignItems="center"
              >
                <Text fontSize="2xs" color="fg.muted">
                  {hasUnsavedChanges
                    ? t('unsavedChanges')
                    : (unchangedLabel ?? t('allChangesSaved'))}
                </Text>
                <HStack gap={2}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (onReject) {
                        onReject();

                        return;
                      }

                      handleClose();
                    }}
                    disabled={isSaving}
                    size="sm"
                    colorScheme={onReject ? 'red' : 'gray'}
                  >
                    {onReject ? tCommon('reject') : t('cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={
                      (!allowSubmitWithoutChanges && !hasUnsavedChanges) ||
                      isSaving
                    }
                    loading={isSaving}
                    size="sm"
                    colorScheme="blue"
                  >
                    {submitLabel ? (
                      <CheckmarkCircle02Icon size={14} />
                    ) : (
                      <FloppyDiskIcon size={14} />
                    )}
                    {submitLabel ?? t('save')}
                  </Button>
                </HStack>
              </HStack>
            ) : (
              <Drawer.CloseTrigger asChild>
                <Button variant="outline" onClick={onClose} size="sm">
                  {t('close')}
                </Button>
              </Drawer.CloseTrigger>
            )}
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Positioner>
      {confirmationDialogs}
    </Drawer.Root>
  );
}
